/* eslint-disable max-lines */
/* eslint-disable max-len */

import { BookmarkIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import AffectedReservationsModal from "../../../components/lines-managements/trips/AffectedReservationsModal";
import CommissionFeeInput from "../../../components/lines-managements/trips/CommissionFeeInput";
import EnhancedDayPicker, { SelectedDateType } from "../../../components/lines-managements/trips/EnhancedDayPicker";
import PatternSelect from "../../../components/lines-managements/trips/PatternSelect";
import SavePatternModal from "../../../components/lines-managements/trips/SavePatternModal";
import DaySelect from "../../../components/lines-managements/trips/SelectDays";
import NewTrip from "../../../components/lines-managements/trips/Trip";
import Schedules from "../../../components/lines-managements/trips/schedules";
import CreateTripWrapper from "../../../components/lines-managements/trips/schedules/CreateTripWrapper";
import FormWrapper from "../../../components/ui/FormWrapper";
import SpinnerLoader from "../../../components/ui/SpinnerLoader";
import SuperAdmin from "../../../components/users/permissions/SuperAdmin";
import useSuperAdmin from "../../../components/users/permissions/useSuperAdmin";
import useFetch from "../../../hooks/useFetch";
import useNavigateState from "../../../hooks/useNavigateState";
import { ILocation } from "../../../interfaces/endpoints/ILocations";
import { ICustomTripWorkingDayPattern } from "../../../interfaces/endpoints/patterns/ICustomTripWorkingDayPattern";
import { CustomTripWorkingDayRequest, CustomTripWorkingDayType, ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import { getLinesSelectList } from "../../../services/Lines.service";
import { getAllLocations } from "../../../services/Locations.service";
import { editTrip, getCurrentTrip, notifyTripChangeEmails, postTrip } from "../../../services/Trips.service";
import "../../../styles/shared-calendar.css";
import { serialize } from "../../../utils/serializeToQueryString";
import TripsSchema from "../../../utils/validations/Trips.schema";

interface LocationState {
  page: number;
}

// Add this interface for the affected reservations modal
interface AffectedReservation {
  email: string;
  reservationId: string;
  hasActiveConcierge: boolean;
  conciergeWhatsAppNumber: string;
  journeyDate: string; // Changed from optional to required to match backend
}

const CreateTrip: React.FC<{ editMode: boolean }> = ({ editMode }) => {
  const isSuperAdmin = useSuperAdmin();
  const { id } = useParams();
  const { state } = useLocation() as { state: LocationState };
  // const [searchParams] = useSearchParams();
  const stateCrumbs = useNavigateState(["Menaxhimi i linjave", "Oraret"]);
  const navigate = useNavigate();
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { response: linesRes } = useFetch(getLinesSelectList, {
    initRequest: !editMode,
    params: { pageIndex: 1, pageSize: 999999999 }
  });

  const { response: tripRes } = useFetch(getCurrentTrip, {
    initRequest: editMode,
    params: Number(id)
  });

  // Working days type (ExceptionDate vs. SelectedWorkingDate)
  const [workingDaysType, setWorkingDaysType] = useState<CustomTripWorkingDayType>(
    CustomTripWorkingDayType.ExceptionDate
  );

  // Using EnhancedDayPicker"s state (SelectedDateType[])
  const [selectedDatesCustom, setSelectedDatesCustom] = useState<SelectedDateType[]>([]);

  // State for affected reservations modal
  const [showAffectedReservationsModal, setShowAffectedReservationsModal] = useState(false);
  const [affectedReservations, setAffectedReservations] = useState<AffectedReservation[]>([]);
  const [currentTripId, setCurrentTripId] = useState<number | null>(null);

  // Helper function to group continuous dates into ranges
  const groupContinuousDates = (dates: Date[]): SelectedDateType[] => {
    if (!dates.length) return [];

    // Remove duplicates by converting to timestamp strings and back
    const uniqueDates = Array.from(new Set(dates.map((d) => d.getTime())))
      .map((timestamp) => new Date(timestamp))
      .sort((a, b) => a.getTime() - b.getTime());

    type DateRange = {
      startDate: Date;
      dates: CustomTripWorkingDayRequest["date"][];
      rangeId: SelectedDateType["rangeId"];
    };

    const ranges: DateRange[] = [];
    let currentRange: Date[] = [uniqueDates[0]];
    let rangeId = Math.random().toString(36).substr(2, 9);

    for (let i = 1; i < uniqueDates.length; i++) {
      const currentDate = uniqueDates[i];
      const prevDate = uniqueDates[i - 1];

      // Check if dates are consecutive (24 hours apart)
      if (currentDate.getTime() - prevDate.getTime() === 24 * 60 * 60 * 1000) {
        currentRange.push(currentDate);
      } else {
        // Store the completed range or single date
        if (currentRange.length > 0) {
          ranges.push({
            startDate: currentRange[0],
            dates: [...currentRange],
            rangeId
          });
        }

        // Start a new range
        currentRange = [currentDate];
        rangeId = Math.random().toString(36).substr(2, 9);
      }
    }

    // Handle the last range or single date
    if (currentRange.length > 0) {
      ranges.push({
        startDate: currentRange[0],
        dates: [...currentRange],
        rangeId
      });
    }

    // Sort ranges by their start date
    ranges.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    // Convert sorted ranges back to SelectedDateType array
    const result: SelectedDateType[] = [];
    ranges.forEach((range) => {
      if (range.dates.length > 1) {
        range.dates.forEach((date) => {
          result.push({ date, type: "range", rangeId: range.rangeId });
        });
      } else {
        result.push({ date: range.dates[0], type: "single" });
      }
    });

    return result;
  };

  useEffect(() => {
    if (editMode && tripRes && tripRes.data) {
      Object.entries(tripRes.data).forEach(([key, value]) => {
        const trip = key as keyof ITripType;
        setValue(trip, value);
      });
    }
    getAllLocations({ pageSize: 99999999 })
      .then((res) => setLocations(res.data.result))
      .catch((err) => console.log(err));
  }, [tripRes, id]);

  // Pre-load custom dates in edit mode
  useEffect(() => {
    if (editMode && tripRes?.data?.customWorkingDays) {
      const newType = tripRes.data.customWorkingDays[0]?.type || CustomTripWorkingDayType.ExceptionDate;
      setWorkingDaysType(newType);

      // Filter dates based on the working days type
      const relevantDates = tripRes.data.customWorkingDays
        .filter((d: CustomTripWorkingDayRequest) => d.type === newType)
        .map((d: CustomTripWorkingDayRequest) => new Date(d.date));

      // Group continuous dates into ranges
      const groupedDates = groupContinuousDates(relevantDates);
      setSelectedDatesCustom(groupedDates);
    }
  }, [tripRes]);

  // Refetch when working days type changes
  const { handleFunc: refetchTrip } = useFetch(getCurrentTrip, {
    initRequest: false
  });

  const [selectedPattern, setSelectedPattern] = useState<ICustomTripWorkingDayPattern | null>(null);
  const [refreshPatternsKey, setRefreshPatternsKey] = useState(0);

  const handleDateSelectionChange = (dates: SelectedDateType[]) => {
    setSelectedPattern(null);

    // If we're removing a date (double-click), just update with the new selection
    if (dates.length < selectedDatesCustom.length) {
      setSelectedDatesCustom(dates);
      setValue(
        "customWorkingDays",
        dates.map((d) => ({
          type: workingDaysType,
          date: new Date(Date.UTC(
            d.date.getFullYear(),
            d.date.getMonth(),
            d.date.getDate(),
            12, 0, 0, 0
          ))
        }))
      );
      return;
    }

    // For new selections, try to group into ranges
    const allDates = dates.map((d) => d.date);
    const groupedDates = groupContinuousDates(allDates);

    setSelectedDatesCustom(groupedDates);
    setValue(
      "customWorkingDays",
      groupedDates.map((d) => ({
        type: workingDaysType,
        date: new Date(Date.UTC(
          d.date.getFullYear(),
          d.date.getMonth(),
          d.date.getDate(),
          12, 0, 0, 0
        ))
      }))
    );
  };

  const handleWorkingDaysTypeChange = async (newType: CustomTripWorkingDayType) => {
    setSelectedPattern(null);
    setWorkingDaysType(newType);
    setSelectedDatesCustom([]);

    if (editMode && id) {
      setIsLoading(true);
      try {
        const response = await refetchTrip(Number(id));
        if (response?.data?.customWorkingDays) {
          const relevantDates = response.data.customWorkingDays
            .filter((d: CustomTripWorkingDayRequest) => d.type === newType)
            .map((d: CustomTripWorkingDayRequest) => new Date(d.date));

          const groupedDates = groupContinuousDates(relevantDates);
          setSelectedDatesCustom(groupedDates);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePatternSelect = (pattern: ICustomTripWorkingDayPattern | null) => {
    setSelectedPattern(pattern);
    if (!pattern) return;

    const newType = pattern.type;
    setWorkingDaysType(newType);

    const patternDates = pattern.dates.map((d) => new Date(d.date));

    const groupedDates = groupContinuousDates(patternDates);

    setSelectedDatesCustom(groupedDates);

    setValue(
      "customWorkingDays",
      groupedDates.map((d) => ({
        type: newType,
        date: new Date(Date.UTC(
          d.date.getFullYear(),
          d.date.getMonth(),
          d.date.getDate(),
          12, 0, 0, 0
        ))
      }))
    );
  };

  const handlePatternRefresh = useCallback(() => {
    setRefreshPatternsKey((prev) => prev + 1);
  }, []);

  const methods = useForm<ITripType>({
    resolver: yupResolver(TripsSchema),
    defaultValues: {
      operationalInterval: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
      }
    }
  });
  const { register, handleSubmit, setValue } = methods;

  const onSubmit: SubmitHandler<ITripType> = async (data) => {

    const submitData: ITripType = {
      ...data,
      operationalInterval: {
        monday: data.operationalInterval.monday,
        tuesday: data.operationalInterval.tuesday,
        wednesday: data.operationalInterval.wednesday,
        thursday: data.operationalInterval.thursday,
        friday: data.operationalInterval.friday,
        saturday: data.operationalInterval.saturday,
        sunday: data.operationalInterval.sunday
      },
      prices: data.prices,
      routeDescription: data.routeDescription,
      routeName: data.routeName,
      trip: {
        departureTimeString: data.trip.departureTimeString,
        arrivalTimeString: data.trip.arrivalTimeString,
        defaultCurrencyId: data.trip.defaultCurrencyId,
        arrivalTime: data.trip.arrivalTimeString,
        departureTime: data.trip.departureTimeString,
        duration: Math.abs(data.trip.duration),
        lineId: data.trip.lineId,
        isReservable: data.trip.isReservable,
        // isActive: data.trip.isActive,
        id: data.trip.id
      },
      schedules: data?.schedules
      ?  data.schedules
        ?.filter((el) => el)
        .map((el) => ({
          arrivalTime: el.arrivalTimeString,
          departureTime: el.departureTimeString,
          arrivalTimeString: el.arrivalTimeString,
          departureTimeString: el.departureTimeString,
          duration: el.duration,
          stationId: el.stationId,
          tripId: el.tripId,
          id: editMode ? 0 : el.id }))
        : [],
      customWorkingDays: selectedDatesCustom.map((d) => ({
        type: workingDaysType,
        date: new Date(Date.UTC(
          d.date.getFullYear(),
          d.date.getMonth(),
          d.date.getDate(),
          12, 0, 0, 0
        ))
      }))
    };

    if (editMode) {
      await editTrip(submitData)
        .then((res) => {
          toast.success("Orari është ndryshuar me sukses.", { position: "top-right" });

          if (isSuperAdmin && res.data.affected?.affectedPassengers?.length > 0) {
            // Show modal instead of confirmation dialog
            setAffectedReservations(res.data.affected.affectedPassengers);
            setCurrentTripId(res.data.tripId);
            setShowAffectedReservationsModal(true);
          } else {
            // No affected passengers, navigate directly
            setTimeout(() => {
              navigate(`/trips?pageindex=${state?.page ?? 1}`, { state: stateCrumbs });
            }, 500);
          }
        })
        .catch(() => toast.error("Ndodhi një gabim gjatë ndryshimit."));
    } else {
      const lineName =
        linesRes?.data.result.find((el) => el.value === data.trip.lineId)?.label || "";
      await postTrip(submitData)
        .then((res) => {
          toast.success("Orari është publikuar me sukses.", { position: "top-right" });
          setTimeout(() => {
            // const hasParams = searchParams.has("lineId") || searchParams.has("lineName");

            // if (hasParams && lineName.length) {
              navigate(
                {
                  pathname: "/prices",
                  hash: "trips",
                  search: serialize({
                    lineName,
                    lineId: data.trip.lineId,
                    tripId: res.data,
                    tripTime: `${data.trip.departureTimeString} - ${data.trip.arrivalTimeString}`
                  })
                },
                { state: ["Menaxhimi i linjave", "Çmimet"] }
              );
            // } else {
            //   navigate("/trips", { state: stateCrumbs });
            // }
          }, 500);
        })
        .catch(() =>
          toast.error(
            "Ndodhi një gabim gjatë krijimit. Kontrolloni kohën e nisjes dhe arritjes, koha e nisjes nga ndalesat duhet të jetë pas orës së nisjes së linjës.",
            { duration: 6000 }
          )
        );
    }
  };

  const [showSavePatternModal, setShowSavePatternModal] = useState(false);

  const handleNotificationConfirm = async (selectedReservationIds: string[]) => {
    if (!currentTripId) return;

    try {
      // Send notifications only for selected reservations
      const response = await notifyTripChangeEmails(currentTripId, selectedReservationIds);
      if (response.data) {
        toast.success("Njoftimet me email u dërguan me sukses.", { position: "top-right" });
      } else {
        toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
    }

    // Navigate to trips page after sending emails
    setTimeout(() => {
      navigate(`/trips?pageindex=${state?.page ?? 1}`, { state: stateCrumbs });
    }, 500);
  };

  const handleModalClose = () => {
    setShowAffectedReservationsModal(false);
    // Navigate to trips page when user cancels
    setTimeout(() => {
      navigate(`/trips?pageindex=${state?.page ?? 1}`, { state: stateCrumbs });
    }, 500);
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/trips"
        state={["Informatat për oraret"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek oraret</span>
      </Link>
      <FormWrapper
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko orarin" : "Shto një orar të ri"}
        className="mx-0 max-w-full"
      >
        <div className="w-full">
          <NewTrip response={linesRes} editMode={editMode} />
          <Schedules
            locations={locations}
            schedulesRes={tripRes?.data.schedules}
            tripIndex={tripRes?.data.trip.id}
          />
        </div>
        <div className="flex flex-col gap-4 w-full justify-between">
          <CreateTripWrapper title="Ditët e udhëtimit" className="border-none mt-5">
            <div className="space-y-4">
              <div className="flex gap-6 md:px-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="workingDaysType"
                    checked={workingDaysType === CustomTripWorkingDayType.ExceptionDate}
                    onChange={() =>
                      handleWorkingDaysTypeChange(CustomTripWorkingDayType.ExceptionDate)
                    }
                    className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-2 text-gray-700">Përsëritje javore me përjashtime</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="workingDaysType"
                    checked={workingDaysType === CustomTripWorkingDayType.SelectedWorkingDate}
                    onChange={() =>
                      handleWorkingDaysTypeChange(CustomTripWorkingDayType.SelectedWorkingDate)
                    }
                    className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-2 text-gray-700">Datat specifike të punës</span>
                </label>
              </div>
              {isLoading ? (
                <div className="mt-8 mb-4 gap-3 flex justify-center items-center">
                  <p className="text-gray-500 text-center mr-4">Duke u ngarkuar... </p>
                  <SpinnerLoader />
                </div>
              ) : (
                <div className="">
                    {workingDaysType === CustomTripWorkingDayType.ExceptionDate && (
                      <DaySelect register={register} />
                    )}
                    <div className="w-full flex justify-center gap-20">
                      <div className="w-[350px]">
                        <EnhancedDayPicker
                            selectedDates={selectedDatesCustom}
                            onChange={handleDateSelectionChange}
                        />
                      </div>
                      <div className="md:px-4 md:my-16">
                        <div className="flex items-end">
                          <PatternSelect
                            key={refreshPatternsKey}
                            onPatternSelect={handlePatternSelect}
                            value={selectedPattern}
                            onRefreshNeeded={handlePatternRefresh}
                          />
                            <SuperAdmin>
                              <button
                                type="button"
                                onClick={() => setShowSavePatternModal(true)}
                                className="flex items-center border border-gray-300 justify-center w-[62px] h-11 rounded-lg text-primary hover:bg-gray-100 focus:border-primary transition-colors duration-200 ml-4"
                              >
                                <BookmarkIcon className="w-5 h-5" />
                              </button>
                            </SuperAdmin>
                        </div>
                      </div>
                    </div>
                </div>
              )}
            </div>
          </CreateTripWrapper>
          <CreateTripWrapper title="Komisioni" className="border-t border-b-0 pt-5 mt-5">
            <CommissionFeeInput />
          </CreateTripWrapper>
        </div>
      </FormWrapper>
      <SavePatternModal
        show={showSavePatternModal}
        onClose={() => setShowSavePatternModal(false)}
        selectedDates={selectedDatesCustom.map((d) => ({ date: d.date }))}
        workingDaysType={workingDaysType}
        onPatternCreated={handlePatternRefresh}
      />
      {isSuperAdmin && (
        <AffectedReservationsModal
          isOpen={showAffectedReservationsModal}
          onClose={handleModalClose}
          onConfirm={handleNotificationConfirm}
          affectedReservations={affectedReservations}
          tripId={currentTripId || 0}
        />
      )}
    </FormProvider>
  );
};

export default CreateTrip;
